import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import type { APIData, RequestError } from 'webapp-types';
import { BillingAddonType, BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getFormattedPrice } from 'webapp/utils/number';

import BarLoaderComponent from 'webapp/components/bar-loader';
import BillingQuantifiableAddonUpdateBaseComponent, {
	type Args as BaseArgs,
} from 'webapp/components/billing/quantifiable-addon/update-base';
import BillingQuantifiableAddonUpdateWrapperComponent from 'webapp/components/billing/quantifiable-addon/update-wrapper';

const MAX_PACKS = 50;
const PREVIEW_DEBOUNCE_MS = 400;

interface Args extends BaseArgs {
	// lets the host modal recenter itself once the preview changes its height
	scheduleReposition?: () => void;
}

export default class BillingClientPacksUpdateComponent extends BillingQuantifiableAddonUpdateBaseComponent<Args> {
	initialQuantity = this.billingService.subscription?.clientPackItem?.quantity || 0;
	@tracked quantity = this.initialQuantity;

	@tracked subscriptionPreview?: APIData.Billing.SubscriptionPreview;
	@tracked requestInProgress = true;

	// monotonic id used to discard out-of-order preview responses
	private previewRequestId = 0;

	addonType = BillingAddonType.CLIENT_PACK;
	successMessage = 'Account packs updated!';

	openedEventName = TrackingEvent.ADD_PACKS_POPUP_OPENED;
	completedEventName = TrackingEvent.ADD_PACKS_POPUP_COMPLETED;

	buildOpenedProps(): APIData.Tracking.Data {
		return {
			plan: this.activePlan?.id,
			current_packs: this.billingService.subscription?.clientPackItem?.quantity ?? 0,
			current_clients: this.billingService.clientsCurrent,
		};
	}

	buildCompletedProps(): APIData.Tracking.Data {
		return {
			old_packs: this.initialQuantity,
			new_packs: this.quantity,
			added_packs: this.delta,
			price: this.previewPricesForCycle?.amountDue ?? 0,
		};
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// warm up the preview as soon as the editor opens
		next(this, this.requestSubscriptionPreviewData);
	}

	// keep the confirm disabled while the preview (amount due) is loading
	override get isConfirmDisabled(): boolean {
		return super.isConfirmDisabled || this.requestInProgress;
	}

	get pricePerPackFormatted(): string {
		return getFormattedPrice(this.pricePerPackCents, 2);
	}

	get packsTotalFormatted(): string {
		return getFormattedPrice(this.quantity * this.pricePerPackCents, 2);
	}

	// packs difference between what's active now and what's selected
	get delta(): number {
		return this.quantity - this.initialQuantity;
	}

	get deltaFormatted(): string {
		return this.formatCount(this.delta, true);
	}

	get packsCountLabel(): string {
		return `${this.quantity} account ${this.quantity === 1 ? 'pack' : 'packs'}`;
	}

	// "monthly" / "quarterly" / "yearly" for the active plan's billing cycle
	get billingCycleTextLong(): string {
		return this.activePlan?.billingCycleTextLong ?? 'monthly';
	}

	get prorationLabel(): string {
		const count = Math.abs(this.delta);
		return `${this.deltaFormatted} account ${count === 1 ? 'pack' : 'packs'} (pro-rated)`;
	}

	// features count (base plus all selected packs)
	get clientsTotalFormatted(): string {
		const base =
			((this.activePlan?.getFeatureValue(BillingFeature.CLIENTS) as number) || 0) +
			this.billingService.clientsGivenInPA;
		return this.formatCount(base + this.getCountInPacks(BillingFeature.CLIENTS));
	}

	get sourcesTotalFormatted(): string {
		const base =
			((this.activePlan?.getFeatureValue(BillingFeature.SOURCES) as number) || 0) +
			this.billingService.sourcesGivenInPA;
		return this.formatCount(base + this.getCountInPacks(BillingFeature.SOURCES));
	}

	get aiCreditsTotalFormatted(): string {
		const base =
			((this.activePlan?.getFeatureValue(BillingFeature.AI_CREDITS) as number) || 0) +
			this.billingService.aiCreditsGivenInPA;
		return this.formatCount(base + this.getCountInPacks(BillingFeature.AI_CREDITS));
	}

	// when the agency is over its client/source limits after a client-pack downgrade,
	// warn how far over they are and how many extra packs would clear it
	get agencyDowngradeLimitsBreached() {
		return this.billingService.agencyClientPackDowngradeLimitsBreached;
	}

	get clientsOverLimit(): number {
		return this.getOverLimit(BillingFeature.CLIENTS);
	}

	get sourcesOverLimit(): number {
		return this.getOverLimit(BillingFeature.SOURCES);
	}

	// smallest number of extra client packs that covers both overages
	get suggestedExtraPacks(): number {
		return Math.max(
			this.packsToCover(BillingFeature.CLIENTS, this.clientsOverLimit),
			this.packsToCover(BillingFeature.SOURCES, this.sourcesOverLimit),
		);
	}

	get suggestedExtraPacksLabel(): string {
		return `${this.suggestedExtraPacks} account ${this.suggestedExtraPacks === 1 ? 'pack' : 'packs'}`;
	}

	// "2 Client accounts and 1 Data Sources" — how far over each breached limit
	get overLimitText(): string {
		return Object.values(this.agencyDowngradeLimitsBreached ?? {})
			.map((limit) => `${limit.current - limit.max} ${limit.name}`)
			.join(' and ');
	}

	private getOverLimit(feature: BillingFeature.CLIENTS | BillingFeature.SOURCES): number {
		const limit = this.agencyDowngradeLimitsBreached?.[feature];
		return limit ? Math.max(limit.current - limit.max, 0) : 0;
	}

	private packsToCover(
		feature: BillingFeature.CLIENTS | BillingFeature.SOURCES,
		over: number,
	): number {
		const perPack = this.featuresPerPack(feature);
		return over > 0 && perPack > 0 ? Math.ceil(over / perPack) : 0;
	}

	// features gained/lost by this edit (change vs the currently active packs)
	get clientsChange(): number {
		return this.getChangeInPacks(BillingFeature.CLIENTS);
	}

	get sourcesChange(): number {
		return this.getChangeInPacks(BillingFeature.SOURCES);
	}

	get aiCreditsChange(): number {
		return this.getChangeInPacks(BillingFeature.AI_CREDITS);
	}

	get clientsChangeFormatted(): string {
		return this.formatCount(this.clientsChange, true);
	}

	get sourcesChangeFormatted(): string {
		return this.formatCount(this.sourcesChange, true);
	}

	get aiCreditsChangeFormatted(): string {
		return this.formatCount(this.aiCreditsChange, true);
	}

	// "Due today" figures from the subscription preview, for the active billing cycle
	get previewPricesForCycle(): APIData.Billing.SubscriptionPricePreview | undefined {
		const billingCycle = this.activePlan?.billingCycle;
		return billingCycle ? this.subscriptionPreview?.prices[billingCycle] : undefined;
	}

	get prorationFormatted(): string {
		const proration = this.previewPricesForCycle?.proration;
		return typeof proration === 'number' ? getFormattedPrice(proration, 2) : '';
	}

	get balanceFormatted(): string {
		const balance = this.previewPricesForCycle?.balance;
		return balance ? getFormattedPrice(balance, 2) : '';
	}

	get amountDueFormatted(): string {
		const amountDue = this.previewPricesForCycle?.amountDue;
		return typeof amountDue === 'number' ? getFormattedPrice(amountDue, 2) : '';
	}

	// on a plain trial (not a higher-plan trial) nothing is charged today, so hide the section
	get hideDueToday(): boolean {
		return this.billingService.isTrial && !this.billingService.isHigherPlanTrial;
	}

	@action
	override onSliderChange(value: number): void {
		this.quantity = value;
		this.requestInProgress = true;
		debounce(this, this.requestSubscriptionPreviewData, PREVIEW_DEBOUNCE_MS);
	}

	requestSubscriptionPreviewData(): void {
		if (this.isDestroying || this.isDestroyed) {
			return;
		}

		// nothing consumes the preview when the "Due today" section is hidden (plain
		// trial) — skip the request and clear the flag so confirm isn't left disabled
		if (this.hideDueToday) {
			this.requestInProgress = false;
			return;
		}

		// requests can overlap (and the service caches by quantity, so a later one
		// may resolve first) — tag each and only apply the most recent
		const requestId = ++this.previewRequestId;
		this.requestInProgress = true;

		this.requestSubscriptionPreview()
			.then((reply) => {
				if (requestId === this.previewRequestId) {
					this.subscriptionPreview = reply;
				}
			})
			.catch((errorData: RequestError) => {
				if (requestId === this.previewRequestId) {
					// drop stale figures so we never show the wrong amount due
					this.subscriptionPreview = undefined;
					this.requestService.handleGenericThrownError(errorData);
				}
			})
			.finally(() => {
				if (requestId !== this.previewRequestId) {
					return; // superseded by a newer request still in flight
				}
				this.requestInProgress = false;
				// the preview rows change the popup height — recenter it
				this.args.scheduleReposition?.();
			});
	}

	requestSubscriptionPreview() {
		return this.billingService.requestSubscriptionPreview(this.activePlan!.productId, [
			{ id: BillingAddonType.CLIENT_PACK, quantity: this.quantity },
		]);
	}

	private getCountInPacks(
		feature: BillingFeature.CLIENTS | BillingFeature.SOURCES | BillingFeature.AI_CREDITS,
	): number {
		return this.featuresPerPack(feature) * this.quantity;
	}

	private getChangeInPacks(
		feature: BillingFeature.CLIENTS | BillingFeature.SOURCES | BillingFeature.AI_CREDITS,
	): number {
		return this.featuresPerPack(feature) * this.delta;
	}

	private featuresPerPack(
		feature: BillingFeature.CLIENTS | BillingFeature.SOURCES | BillingFeature.AI_CREDITS,
	): number {
		return this.billingService.featuresInOneClientPack[feature] || 0;
	}

	<template>
		<BillingQuantifiableAddonUpdateWrapperComponent
			@quantity={{this.quantity}}
			@max={{MAX_PACKS}}
			@confirmed={{this.confirmed}}
			@confirmName="confirmClientPacks"
			@confirmLabel="I confirm this purchase of account packs and acknowledge Databox will invoice me as shown above."
			@finePrint="You'll be charged today for any prorated difference for the rest of your term. After that, it renews together with your plan subscription until you adjust or cancel it."
			@isConfirmDisabled={{this.isConfirmDisabled}}
			@submitInProgress={{this.submitInProgress}}
			@onSliderChange={{this.onSliderChange}}
			@onConfirmChange={{this.onConfirmChange}}
			@onSave={{this.save}}
			@onClose={{@onClose}}
		>
			<:count>
				{{this.packsCountLabel}}
			</:count>
			<:beforeSlider>
				{{#if this.agencyDowngradeLimitsBreached}}
					<div class="quantifiable-addon__warning">
						Your account is over the limits included in your current plan by
						<b>{{this.overLimitText}}</b>. Add at least
						<b>{{this.suggestedExtraPacksLabel}}</b>
						to get back within your limits.
					</div>
				{{/if}}
			</:beforeSlider>
			<:summary>
				<div class="quantifiable-addon__features">
					<div class="feature">
						<span>Accounts</span>
						<div>
							<b>{{this.clientsTotalFormatted}}</b>
							{{#if this.clientsChange}}
								({{this.clientsChangeFormatted}})
							{{/if}}
						</div>
					</div>
					<div class="feature">
						<span>Data sources</span>
						<div>
							<b>{{this.sourcesTotalFormatted}}</b>
							{{#if this.sourcesChange}}
								({{this.sourcesChangeFormatted}})
							{{/if}}
						</div>
					</div>
					<div class="feature">
						<span>AI credits/mo</span>
						<div>
							<b>{{this.aiCreditsTotalFormatted}}</b>
							{{#if this.aiCreditsChange}}
								({{this.aiCreditsChangeFormatted}})
							{{/if}}
						</div>
					</div>
				</div>

				{{#if this.isDirty}}
					<div class="quantifiable-addon__summary">
						<div class="quantifiable-addon__summary-section">
							<div class="quantifiable-addon__summary-title">Summary</div>

							<div class="line">
								<span class="label">
									Account packs
									{{this.initialQuantity}}
									→
									{{this.quantity}}
									{{#if this.delta}}
										<small> ({{this.deltaFormatted}})</small>
									{{/if}}
								</span>
							</div>

							<div class="line">
								<span class="label">
									{{this.packsCountLabel}}
									×
									{{this.pricePerPackFormatted}}
								</span>
								<span class="value">{{this.packsTotalFormatted}}</span>
							</div>

							<div class="line line--total">
								<span class="label">New {{this.billingCycleTextLong}} total</span>
								<span class="value"><b>{{this.packsTotalFormatted}}</b></span>
							</div>
						</div>

						{{#unless this.hideDueToday}}
							<div class="quantifiable-addon__summary-section">
								<div class="quantifiable-addon__summary-title">Due today</div>

								{{#if this.requestInProgress}}
									<div class="loader__wrapper">
										<BarLoaderComponent @size="tiny" />
									</div>
								{{else}}
									<div class="line">
										<span class="label">{{this.prorationLabel}}</span>
										<span class="value">{{this.prorationFormatted}}</span>
									</div>
									{{#if this.balanceFormatted}}
										<div class="line">
											<span class="label">Credit balance applied</span>
											<span class="value">{{this.balanceFormatted}}</span>
										</div>
									{{/if}}
									<div class="line line--total">
										<span class="label">Total due today</span>
										<span class="value">
											<b>
												{{this.amountDueFormatted}}
											</b>
										</span>
									</div>
								{{/if}}
							</div>
						{{/unless}}
					</div>
				{{/if}}
			</:summary>
		</BillingQuantifiableAddonUpdateWrapperComponent>
	</template>
}
